import { removeQuotesFromText } from '@whistler-ui/k2/helpers/textHelpers';
import { SilentParam } from '@whistler-ui/k2/types/common';

import { API_ENDPOINTS } from '../constants';
import { CreateMemberPayload, Member } from '../types';
import { requestWithOptions } from './helper';

export const getLocalMembers = async (
  params?: SilentParam,
): Promise<Member[]> => {
  const { silent = false } = params || {};
  try {
    const result = await requestWithOptions(`${API_ENDPOINTS.ACCOUNTS}/users`, {
      method: 'GET',
    });

    return result;
  } catch (error) {
    const msg = removeQuotesFromText((error as Error | TypeError).message);
    if (silent) {
      console.warn(`Error handled silently: ${msg}`);
      return [];
    }
    throw error;
  }
};

export const updateMember = async (params: SilentParam<{ member: Member }>) => {
  const { silent = false, member } = params || {};

  try {
    const newMember = await requestWithOptions(
      `${API_ENDPOINTS.ACCOUNTS}/users`,
      {
        body: JSON.stringify(member),
        method: 'PUT',
      },
    );

    return newMember as Member;
  } catch (error) {
    const msg = removeQuotesFromText((error as Error | TypeError).message);
    if (silent) {
      console.warn(`Error handled silently: ${msg}`);
      return null;
    }
    throw error;
  }
};

export const createMember = async (
  params: SilentParam<{
    member: CreateMemberPayload;
  }>,
) => {
  const { silent = false, member } = params || {};

  try {
    const newMember = await requestWithOptions(
      `${API_ENDPOINTS.ACCOUNTS}/users`,
      {
        body: JSON.stringify(member),
        method: 'POST',
      },
    );

    return newMember;
  } catch (error) {
    const msg = removeQuotesFromText((error as Error | TypeError).message);
    if (silent) {
      console.warn(`Error handled silently: ${msg}`);
      return null;
    }
    throw error;
  }
};

export const getGroupMembers = async (
  params?: SilentParam,
): Promise<Member[]> => {
  const { silent = false } = params || {};
  try {
    const result = await requestWithOptions(
      `${API_ENDPOINTS.ACCOUNTS}/users/grouped`,
      {
        method: 'GET',
      },
    );

    return result;
  } catch (error) {
    const msg = removeQuotesFromText((error as Error | TypeError).message);
    if (silent) {
      console.warn(`Error handled silently: ${msg}`);
      return [];
    }
    throw error;
  }
};

export const getUngroupedMembers = async (
  params?: SilentParam,
): Promise<Member[]> => {
  const { silent = false } = params || {};
  try {
    const result = await requestWithOptions(
      `${API_ENDPOINTS.ACCOUNTS}/users/ungrouped`,
      {
        method: 'GET',
      },
    );

    return result;
  } catch (error) {
    const msg = removeQuotesFromText((error as Error | TypeError).message);
    if (silent) {
      console.warn(`Error handled silently: ${msg}`);
      return [];
    }
    throw error;
  }
};

export const updateGroup = async (params: SilentParam<{ member: Member }>) => {
  const { silent = false, member } = params || {};

  try {
    const updatedGroup = await requestWithOptions(
      `${API_ENDPOINTS.ACCOUNTS}/users/group`,
      {
        body: JSON.stringify({ ...member }),
        method: 'PUT',
      },
    );

    return updatedGroup;
  } catch (error) {
    const msg = removeQuotesFromText((error as Error | TypeError).message);
    if (silent) {
      console.warn(`Error handled silently: ${msg}`);
      return null;
    }
    throw error;
  }
};

export const removeMemberFromGroup = async (
  params: SilentParam<{ memberId: string }>,
) => {
  const { silent = false, memberId } = params || {};

  try {
    const result = await requestWithOptions(
      `${API_ENDPOINTS.ACCOUNTS}/users/group/${memberId}`,
      {
        method: 'DELETE',
      },
    );

    return result;
  } catch (error) {
    const msg = removeQuotesFromText((error as Error | TypeError).message);
    if (silent) {
      console.warn(`Error handled silently: ${msg}`);
      return null;
    }
    throw error;
  }
};

export const getCarrierGroups = async (
  params?: SilentParam,
): Promise<{ id: string; name: string }[]> => {
  const { silent = false } = params || {};
  try {
    const result = await requestWithOptions(
      `${API_ENDPOINTS.ACCOUNTS}/users/carrier/group`,
      {
        method: 'GET',
      },
    );

    return result;
  } catch (error) {
    const msg = removeQuotesFromText((error as Error | TypeError).message);
    if (silent) {
      console.warn(`Error handled silently: ${msg}`);
      return [];
    }
    throw error;
  }
};

export const switchGroupCarrier = async (
  params: SilentParam<{ carrierGroupId: string }>,
) => {
  const { silent = false, carrierGroupId } = params || {};

  try {
    const result = await requestWithOptions(
      `${API_ENDPOINTS.ACCOUNTS}/users/switch_carrier/${carrierGroupId}`,
      {
        method: 'POST',
      },
    );

    return result;
  } catch (error) {
    const msg = removeQuotesFromText((error as Error | TypeError).message);
    if (silent) {
      console.warn(`Error handled silently: ${msg}`);
      return null;
    }
    throw error;
  }
};
